
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect, useMemo } from 'react';
import routes from '../../router/config';

export default function ServicesPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Painting Services Cape Town | Interior Exterior Commercial Painting | Professional Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive painting services in Cape Town: interior, exterior, commercial painting, waterproofing, decorative finishes. Professional contractors with 15+ years experience. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'painting services Cape Town, interior painting, exterior painting, commercial painting, waterproofing, decorative painting');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Professional Painting Services Cape Town",
      "description": "Comprehensive painting services including interior, exterior, commercial painting, waterproofing, and decorative finishes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Painting Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Interior Painting",
              "description": "Professional interior painting for residential and commercial properties"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Exterior Painting",
              "description": "Weather-resistant exterior painting with premium materials"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Commercial Painting",
              "description": "Professional painting solutions for businesses and commercial properties"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Professional Painting Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  // Automatically generate service pages from routing configuration
  const servicePages = useMemo(() => {
    return routes
      .filter(route => route.path?.startsWith('/services/') && route.path !== '/services')
      .map(route => {
        const serviceName = route.path!.split('/services/')[1];
        const formattedName = serviceName
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Get appropriate icon based on service type
        const getServiceIcon = (name: string) => {
          if (name.includes('interior')) return 'ri-home-4-line';
          if (name.includes('exterior')) return 'ri-building-2-line';
          if (name.includes('commercial')) return 'ri-community-line';
          if (name.includes('roof')) return 'ri-home-8-line';
          if (name.includes('waterproofing')) return 'ri-droplet-line';
          return 'ri-brush-3-line';
        };

        // Get service description
        const getServiceDescription = (name: string) => {
          if (name.includes('interior')) return 'Transform your indoor spaces with premium quality paints and expert craftsmanship';
          if (name.includes('exterior')) return 'Protect and beautify your property exterior with weather-resistant paints';
          if (name.includes('commercial')) return 'Professional painting services for offices, retail spaces, and commercial buildings';
          if (name.includes('roof')) return 'Extend your roof life and improve energy efficiency with specialized coatings';
          if (name.includes('waterproofing')) return 'Comprehensive waterproofing solutions to protect against water damage';
          return 'Professional painting services with expert craftsmanship';
        };

        return {
          name: formattedName,
          path: route.path!,
          icon: getServiceIcon(serviceName),
          description: getServiceDescription(serviceName)
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const mainServices = [
    {
      icon: 'ri-home-4-line',
      title: 'Residential Interior Painting',
      description: 'Transform your home with our expert residential painting services that bring your vision to life. We specialize in creating beautiful, lasting finishes for every room in your house.',
      features: [
        'Living rooms and bedrooms',
        'Kitchens and bathrooms', 
        'Ceiling treatments',
        'Decorative accent walls',
        'Color consultation',
        'Trim and cabinet refinishing'
      ],
      image: 'https://public.readdy.ai/ai/img_res/288845008715d32729afe8451fac33a6.jpg',
      link: '/services/interior-painting'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Exterior House Painting',
      description: 'Protect and enhance your property\'s curb appeal with our comprehensive exterior painting services using weather-resistant paints and advanced techniques.',
      features: [
        'Complete house exteriors',
        'Roof painting and waterproofing',
        'Deck and fence staining',
        'Pressure washing prep',
        'Weather damage restoration',
        'Protective coatings'
      ],
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20Cape%20Town%20house%20exterior%20with%20fresh%20paint%20job%2C%20professional%20exterior%20painting%2C%20modern%20residential%20property%2C%20pristine%20finish%2C%20coastal%20setting%2C%20professional%20workmanship%2C%20attractive%20curb%20appeal&width=600&height=400&seq=exterior-painting&orientation=landscape',
      link: '/services/exterior-painting'
    },
    {
      icon: 'ri-community-line',
      title: 'Commercial Painting Services',
      description: 'Professional painting solutions for businesses, offices, retail spaces, and industrial properties. We work efficiently to minimize disruption to your operations.',
      features: [
        'Office buildings and complexes',
        'Retail establishments',
        'Industrial facilities',
        'Maintenance programs',
        'Interior and exterior work',
        'Specialty coatings'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20commercial%20office%20building%20with%20professional%20painting%2C%20sleek%20exterior%20finish%2C%20business%20district%20Cape%20Town%2C%20contemporary%20architecture%2C%20high%20quality%20commercial%20painting%20services&width=600&height=400&seq=commercial-painting&orientation=landscape',
      link: '/services/commercial-painting'
    }
  ];

  const specialtyServices = [
    {
      icon: 'ri-brush-3-line',
      title: 'Decorative Finishes',
      description: 'Custom decorative painting techniques including faux finishes, texture applications, and artistic wall treatments.',
      details: [
        'Faux finishing techniques',
        'Textured wall applications',
        'Metallic and pearl finishes',
        'Stenciling and murals',
        'Venetian plaster',
        'Color washing effects'
      ]
    },
    {
      icon: 'ri-hammer-line',
      title: 'Surface Preparation',
      description: 'Comprehensive preparation services ensuring optimal paint adhesion and long-lasting results.',
      details: [
        'Crack and hole repair',
        'Surface cleaning and degreasing',
        'Sanding and smoothing',
        'Primer application',
        'Caulking and sealing',
        'Wallpaper removal'
      ]
    },
    {
      icon: 'ri-droplet-line',
      title: 'Waterproofing Solutions',
      description: 'Professional waterproofing services to protect your property from moisture damage.',
      details: [
        'Roof waterproofing',
        'Basement sealing',
        'Exterior wall protection',
        'Deck waterproofing',
        'Foundation treatments',
        'Moisture barrier installation'
      ]
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Consultation',
      description: 'Expert color consultation to help you choose the perfect palette for your space.',
      details: [
        'Color scheme development',
        'Lighting consideration',
        'Room function analysis',
        'Trend recommendations',
        'Sample applications',
        'Final color approval'
      ]
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance Services',
      description: 'Ongoing maintenance programs to keep your property looking its best year-round.',
      details: [
        'Annual touch-ups',
        'Scheduled inspections',
        'Preventive maintenance',
        'Emergency repairs',
        'Warranty coverage',
        'Long-term care plans'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Restoration Services',
      description: 'Specialized restoration for heritage properties and damaged surfaces.',
      details: [
        'Heritage property restoration',
        'Lead paint removal',
        'Fire damage restoration',
        'Water damage repair',
        'Historical color matching',
        'Conservation techniques'
      ]
    }
  ];

  const paintTypes = [
    {
      name: 'Premium Interior Paints',
      description: 'High-quality interior paints with excellent coverage and durability',
      brands: ['Dulux', 'Plascon', 'Prominent Paints', 'Kansai Plascon']
    },
    {
      name: 'Weather-Resistant Exterior Paints',
      description: 'Specially formulated for Cape Town\'s coastal climate',
      brands: ['Dulux Weatherguard', 'Plascon Velvaglo', 'Prominent Exterior']
    },
    {
      name: 'Eco-Friendly Options',
      description: 'Low-VOC and environmentally conscious paint solutions',
      brands: ['Dulux EasyClean', 'Plascon Eco-Paint', 'Natural Paint Co.']
    },
    {
      name: 'Specialty Coatings',
      description: 'Industrial and commercial grade protective coatings',
      brands: ['Sigma Coatings', 'International Paint', 'Jotun']
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20Cape%20Town%20house%2C%20skilled%20painters%20with%20brushes%20and%20equipment%2C%20high%20quality%20workmanship%2C%20residential%20painting%20services%2C%20modern%20home%20exterior%2C%20professional%20contractors%20at%20work&width=1920&height=600&seq=services-hero&orientation=landscape"
            alt="Professional painting services in Cape Town"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Painting Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Comprehensive painting solutions for residential, commercial, and industrial properties across Cape Town. From interior transformations to exterior protection, we deliver exceptional results.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Our Core Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We offer comprehensive painting solutions designed to meet every need, from intimate residential projects to large-scale commercial developments.
            </p>
          </div>

          <div className="space-y-20">
            {mainServices.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mb-8" style={{backgroundColor: '#ffff00'}}>
                    <i className={`${service.icon} text-3xl text-black`}></i>
                  </div>
                  <h3 className="text-3xl font-bold text-black mb-6">{service.title}</h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">{service.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <i className="ri-check-double-line text-green-500 mr-3 text-lg"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-4">
                    <Link
                      to={service.link}
                      className="bg-yellow-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Learn More
                    </Link>
                    <Link
                      to="/contact"
                      className="bg-gray-100 text-black px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Get Quote
                    </Link>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-xl object-cover object-top w-full h-[400px]"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Services Grid - Automatically Generated */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">All Our Painting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of professional painting services across Cape Town
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicePages.map((service, index) => (
              <Link
                key={index}
                to={service.path}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow cursor-pointer group border border-gray-100"
              >
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-yellow-600 transition-colors">{service.name}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                
                <div className="inline-flex items-center text-black font-semibold group-hover:text-yellow-600 transition-colors">
                  Learn More
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Interior Painting',
                description: 'Transform your indoor spaces with premium quality paints and expert craftsmanship. We handle everything from preparation to final touches.',
                features: ['Premium paint brands', 'Color consultation', 'Furniture protection', 'Clean finish'],
                link: '/services/interior-painting'
              },
              {
                icon: 'ri-building-line',
                title: 'Exterior Painting',
                description: 'Protect and beautify your property exterior with weather-resistant paints and professional application techniques.',
                features: ['Weather-resistant paints', 'Surface preparation', 'Pressure washing', 'Long-lasting finish'],
                link: '/services/exterior-painting'
              },
              {
                icon: 'ri-community-line',
                title: 'Commercial Painting',
                description: 'Professional painting services for offices, retail spaces, and commercial buildings with minimal business disruption.',
                features: ['Flexible scheduling', 'Commercial-grade paints', 'Safety compliance', 'Project management'],
                link: '/services/commercial-painting'
              },
              {
                icon: 'ri-home-8-line',
                title: 'Roof Painting',
                description: 'Extend your roof life and improve energy efficiency with specialized roof painting and restoration services.',
                features: ['Roof restoration', 'Energy-efficient coatings', 'Leak prevention', 'UV protection'],
                link: '/services/roof-painting'
              },
              {
                icon: 'ri-drop-line',
                title: 'Waterproofing',
                description: 'Comprehensive waterproofing solutions to protect your property from water damage and moisture issues.',
                features: ['Basement waterproofing', 'Roof waterproofing', 'Crack sealing', 'Moisture control'],
                link: '/services/waterproofing'
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-2 mb-8">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-yellow-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link
                  to={service.link}
                  className="inline-flex items-center text-black font-semibold hover:text-yellow-600 transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Specialty Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Additional services to meet all your painting and maintenance needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-palette-line',
                title: 'Color Consultation',
                description: 'Expert advice on color schemes and paint selection'
              },
              {
                icon: 'ri-brush-3-line',
                title: 'Texture Painting',
                description: 'Decorative finishes and textured wall treatments'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Paint Protection',
                description: 'Protective coatings for high-traffic areas'
              },
              {
                icon: 'ri-recycle-line',
                title: 'Eco-Friendly Options',
                description: 'Environmentally conscious paint solutions'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paint Types & Brands */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Premium Paint Brands & Materials
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We use only the finest paint brands and materials to ensure exceptional quality and longevity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintTypes.map((type, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <h3 className="text-lg font-bold text-black mb-3">{type.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{type.description}</p>
                <div className="space-y-1">
                  {type.brands.map((brand, idx) => (
                    <div key={idx} className="text-xs text-gray-500 font-medium">{brand}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Professional Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From consultation to completion, we follow a proven process that ensures exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Consultation & Assessment',
                description: 'Detailed site visit to understand your needs, assess surfaces, and provide recommendations.'
              },
              {
                step: '02',
                title: 'Detailed Quotation',
                description: 'Comprehensive quote with transparent pricing, materials list, and project timeline.'
              },
              {
                step: '03',
                title: 'Preparation & Execution',
                description: 'Thorough surface preparation followed by professional application using premium materials.'
              },
              {
                step: '04',
                title: 'Quality Control & Handover',
                description: 'Final inspection, touch-ups if needed, and complete project handover with warranty.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h4 className="text-xl font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Service Areas Across Cape Town
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide professional painting services throughout Cape Town and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              'Atlantic Beach Estate',
              'Big Bay',
              'Bloubergstrand',
              'Bothasig',
              'Camps Bay',
              'Sea Point',
              'Clifton',
              'Constantia',
              'Hout Bay',
              'Green Point'
            ].map((area, index) => (
              <Link
                key={index}
                to={`/areas/${area.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg hover:bg-yellow-50 transition-all cursor-pointer group border border-gray-100"
              >
                <i className="ri-map-pin-2-line text-3xl text-black mb-3 group-hover:scale-110 transition-transform w-12 h-12 flex items-center justify-center mx-auto" style={{backgroundColor: '#ffff00', borderRadius: '50%'}}></i>
                <h3 className="font-semibold text-black group-hover:text-yellow-600 transition-colors text-sm">
                  {area}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Start Your Painting Project?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Contact Cape Town's most trusted painting contractors today for a comprehensive consultation and discover why property owners choose us for their most important projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
